// Custom React hook for managing socket connections and handling real-time events.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { accessToken } from "../utils/localData";
import { socket } from "../socket";
import { selectGeneral, setIsLoggedIn } from "../redux/slices/generalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetProjectByIdQuery } from "../redux/slices/api/projectsApiSlice";
import { useGetNotificationsQuery } from "../redux/slices/api/userApi";

// Main hook function
const useSocketConnection = () => {
  const location = useLocation();
  // Destructure values from the Redux store using useSelector
  const { isLoggedIn } = useSelector(selectGeneral);

  // Don't fetch notifications on admin pages
  const isAdminPage = location.pathname.startsWith("/admin");

  // Custom hook for fetching notifications
  // Note: First arg is query params (undefined), second is options (with skip)
  const { refetch: refetchNotification } = useGetNotificationsQuery(undefined, {
    skip: !isLoggedIn || isAdminPage,
  });

  // Custom hook for triggering a lazy project data fetch
  const [triggerGetProjectData] = useLazyGetProjectByIdQuery();

  // Redux dispatcher
  const dispatch = useDispatch();

  // Effect hook for handling socket connections and cleanup
  useEffect(() => {
    // Event handler for incoming notifications
    const onNotification = () => {
      refetchNotification();
    };

    // Event handler for real-time project data updates
    const onRefetchProjectById = async (id) => {
      try {
        // Trigger a lazy project data fetch using the provided ID
        await triggerGetProjectData({
          id,
        }).unwrap();
      } catch (error) {
        // Handle errors if necessary
      }
    };

    // Check if the user is authenticated (has an access token)
    if (accessToken()) {
      // Set the user as logged in in the Redux store
      dispatch(setIsLoggedIn(true));

      // Subscribe to the "notification" event to handle incoming notifications
      socket.on("notification", onNotification);
    }

    // Cleanup: Unsubscribe from socket events when the component unmounts
    return () => {
      socket.off("notification", onNotification);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // The hook doesn't return any component elements
  // It is used for its side effects related to socket connections
  return null;
};

// Export the custom hook as the default export
export default useSocketConnection;
